import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiWifi, FiMonitor, FiShield, FiVolume2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Sanctuary = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const features = [
    {
      icon: FiMonitor,
      title: 'Laptop Workspaces',
      description: 'Dedicated workstations for digital nomads with ergonomic seating'
    },
    {
      icon: FiWifi,
      title: 'High-Speed WiFi',
      description: 'Reliable internet connectivity throughout the property'
    },
    {
      icon: FiShield,
      title: 'Hot Water & Safety',
      description: '24/7 hot water supply and comprehensive safety measures'
    },
    {
      icon: FiVolume2,
      title: 'Sound-Insulated',
      description: 'Peaceful rooms designed for ultimate tranquility'
    }
  ];

  const rooms = [
    {
      name: 'Mountain View Suite',
      image: 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: ['King Bed', 'Mountain Views', 'Private Balcony', 'Work Desk'],
      price: 'LKR 8,500'
    },
    {
      name: 'Garden Sanctuary',
      image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: ['Queen Bed', 'Garden Views', 'Terrace Access', 'Reading Nook'],
      price: 'LKR 7,000'
    },
    {
      name: 'Riverside Retreat',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: ['Twin Beds', 'River Sounds', 'Nature Views', 'Meditation Corner'],
      price: 'LKR 6,500'
    }
  ];

  return (
    <section id="sanctuary" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Your <span className="text-gradient">Sanctuary</span> Awaits
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Thoughtfully designed accommodations where modern comfort meets pristine nature
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="glass-card p-6 text-center hover-glow"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 + index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="w-12 h-12 glass-morphism rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={feature.icon} className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-300 text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Room Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rooms.map((room, index) => (
            <motion.div
              key={room.name}
              className="glass-card overflow-hidden hover-glow"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.0 + index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative overflow-hidden">
                <motion.img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-48 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute top-4 right-4">
                  <div className="glass-morphism px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-semibold">{room.price}/night</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-3 font-['Playfair_Display']">
                  {room.name}
                </h3>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {room.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <motion.button
                  className="w-full py-3 glass-morphism text-white font-semibold rounded-lg hover-glow"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  View Details
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Direct Booking CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.6 }}
        >
          <div className="glass-card p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4 font-['Playfair_Display']">
              Join the Serenity Club
            </h3>
            <p className="text-gray-300 mb-6">
              Sign up for exclusive access to member rates and skip the OTA commissions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 glass-morphism text-white placeholder-gray-400 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <motion.button
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover-glow"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get 10% Off
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Sanctuary;